import React from 'react'
import { Box, Card, CardHeader, Typography } from '@mui/material'
import { AppButtonIcon, AppCardTitle, AppToggleButton } from '@components'
import { dataColors, dsColorNew } from '@palette'

const COLORS = [dsColorNew.appBlue, dataColors.emerald500, dataColors.violet500, dataColors.pink500, dataColors.sky500]
const W = 1000
const H = 220
const PAD = { top: 12, right: 24, bottom: 28, left: 48 }

// The analyzers' prices chart (AnalyzerPricesChart) as the tour demo draws it: same card, title and
// toolbar ("Price values" / "Variation (%)", legend and fullscreen buttons), lines for LOR / DOR and
// columns for the other analyzers, one series per site.
export default function AnalyzerDemoChart({ title, xLabels, series, type = 'column', extraAction = null, decimals = 2 }) {
	const values = series.flatMap(s => s.values)
	const max = Math.max(...values) * 1.08
	const min = type === 'line' ? Math.min(...values) * 0.9 : 0
	const plotW = W - PAD.left - PAD.right
	const plotH = H - PAD.top - PAD.bottom
	const x = i => PAD.left + (plotW / xLabels.length) * (i + 0.5)
	const y = v => PAD.top + plotH - ((v - min) / (max - min)) * plotH
	const groupW = plotW / xLabels.length
	const barW = Math.min(28, (groupW * 0.7) / series.length)

	return <Card data-tour-id="tour-prices-chart">
		<CardHeader
			title={<AppCardTitle icon="chart-line" title={title}/>}
			action={<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				{extraAction}
				<AppToggleButton value="price" onChange={() => {}} tabs={[{ value: 'price', label: 'Price values' }, { value: 'diff', label: 'Variation (%)' }]}/>
				<AppButtonIcon iconProps={{ iconWeight: 'solid' }} tooltip="Show/Hide Legend" icon="eye-slash"/>
				<AppButtonIcon iconProps={{ iconWeight: 'solid' }} tooltip="Fullscreen" icon="expand-wide" data-tour-id="tour-chart-fullscreen"/>
			</Box>}
		/>
		<Box sx={{ px: 2 }}>
			<svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block' }}>
				{[0, 0.25, 0.5, 0.75, 1].map(t => {
					const v = min + (max - min) * t
					return <g key={t}>
						<line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke={dsColorNew.grayBorder} strokeWidth="1"/>
						<text x={PAD.left - 8} y={y(v) + 4} fontSize="11" textAnchor="end" fill={dsColorNew.textMuted}>{v.toFixed(decimals === 1 ? 1 : 0)}</text>
					</g>
				})}
				{xLabels.map((label, i) => <text key={label} x={x(i)} y={H - 8} fontSize="11" textAnchor="middle" fill={dsColorNew.textMuted}>{label}</text>)}
				{type === 'line'
					? series.map((s, si) => <g key={s.name}>
						<polyline fill="none" stroke={COLORS[si % COLORS.length]} strokeWidth="2" points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}/>
						{s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="3.5" fill={COLORS[si % COLORS.length]}/>)}
					</g>)
					: series.map((s, si) => s.values.map((v, i) => <rect
						key={`${s.name}-${i}`}
						x={x(i) - (barW * series.length) / 2 + barW * si}
						y={y(v)}
						width={barW - 2}
						height={y(min) - y(v)}
						rx="2"
						fill={COLORS[si % COLORS.length]}
					/>))}
			</svg>
		</Box>
		<Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, pb: 2, pt: 1 }}>
			{series.map((s, si) => <Box key={s.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
				<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[si % COLORS.length] }}/>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{s.name}</Typography>
			</Box>)}
		</Box>
	</Card>
}
